import { useEffect, useRef } from "react"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { cn } from "@/lib/utils"
import { getTurnstileSiteKey, isTurnstileRequired } from "@/lib/turnstile"

type CloudflareTurnstileGateProps = {
  action: string
  resetKey: number
  onTokenChange: (token: string | null) => void
  className?: string
}

/**
 * Owns the production-only Turnstile UI policy for auth-sensitive actions.
 * The parent keeps the token so it can block submits, while this component
 * handles token expiry, script errors, and reset after one-time token use.
 */
export function CloudflareTurnstileGate({
  action,
  resetKey,
  onTokenChange,
  className,
}: CloudflareTurnstileGateProps) {
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined)
  const required = isTurnstileRequired()
  const siteKey = getTurnstileSiteKey()

  useEffect(() => {
    if (!required || resetKey === 0) return

    onTokenChange(null)
    turnstileRef.current?.reset()
  }, [onTokenChange, required, resetKey])

  if (!required) return null

  if (!siteKey) {
    return (
      <p className={cn("rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive", className)}>
        Human verification is not configured.
      </p>
    )
  }

  return (
    <div className={cn("flex min-h-[65px] justify-center", className)}>
      <Turnstile
        ref={turnstileRef}
        siteKey={siteKey}
        options={{
          action,
          size: "flexible",
          theme: "auto",
        }}
        onSuccess={(token) => onTokenChange(token)}
        onExpire={() => onTokenChange(null)}
        onError={() => onTokenChange(null)}
        onTimeout={() => onTokenChange(null)}
      />
    </div>
  )
}
